// World Bank API Service for Silk Road Terminal
// Fetches economic indicators from the World Bank Data API

import { cacheService, DEFAULT_TTL } from '../data/cacheService';
import {
  WB_INDICATOR_CODES,
  COUNTRY_CODES,
  getWBCountryCode,
  type APIResponse,
} from './types';

const WB_BASE_URL = 'https://api.worldbank.org/v2';
const PER_PAGE = 100;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Data point interface
export interface WBDataPoint {
  year: number;
  value: number | null;
}

// Country profile data
export interface WBCountryProfile {
  gdp: number | null;
  gdpGrowth: number | null;
  gdpPerCapita: number | null;
  fdiInflows: number | null;
  fdiGdp: number | null;
  population: number | null;
  populationGrowth: number | null;
  inflation: number | null;
  reserves: number | null;
  externalDebt: number | null;
  externalDebtGdp: number | null;
  currentAccount: number | null;
  exportsGdp: number | null;
  importsGdp: number | null;
  tradeGdp: number | null;
  govtDebtGdp: number | null;
  lastUpdated: Date;
  dataYear: number;
  [key: string]: number | null | Date; // Index signature for dynamic access
}

// Sleep helper for retry delays
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Parse World Bank API response
const parseWBResponse = (data: unknown[][]): Array<{ year: number; value: number | null }> => {
  if (!data || data.length < 2 || !Array.isArray(data[1])) {
    return [];
  }

  const results: Array<{ year: number; value: number | null }> = [];

  for (const item of data[1]) {
    if (item && typeof item === 'object' && 'date' in item) {
      const year = parseInt(String(item.date), 10);
      const value = item.value !== null && item.value !== undefined
        ? Number(item.value)
        : null;

      if (!isNaN(year)) {
        results.push({ year, value });
      }
    }
  }

  // Sort by year descending (most recent first)
  return results.sort((a, b) => b.year - a.year);
};

// Fetch with retry logic
const fetchWithRetry = async (
  url: string,
  retries: number = MAX_RETRIES
): Promise<unknown[][]> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`World Bank API attempt ${attempt}/${retries} failed:`, lastError.message);

      if (attempt < retries) {
        await sleep(RETRY_DELAY * attempt); // Exponential backoff
      }
    }
  }

  throw lastError || new Error('Failed to fetch from World Bank API');
};

/**
 * Fetch a single indicator for a country over a time range
 */
export const fetchIndicator = async (
  countryCode: string,
  indicatorCode: string,
  startYear: number = 2000,
  endYear: number = new Date().getFullYear()
): Promise<APIResponse<WBDataPoint[]>> => {
  const wbCountry = getWBCountryCode(countryCode);
  const cacheKey = `wb_indicator_${wbCountry}_${indicatorCode}_${startYear}_${endYear}`;

  // Check cache with stale-while-revalidate
  const cached = cacheService.getStale<WBDataPoint[]>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  // If we have stale data, return it while fetching fresh
  const staleData = cached.data;

  try {
    const url = `${WB_BASE_URL}/country/${wbCountry}/indicator/${indicatorCode}?format=json&per_page=${PER_PAGE}&date=${startYear}:${endYear}`;
    const response = await fetchWithRetry(url);
    const data = parseWBResponse(response);

    // Cache the fresh data
    cacheService.set(cacheKey, data, DEFAULT_TTL);

    return {
      data,
      error: null,
      source: 'live',
      lastUpdated: new Date(),
      isStale: false,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // Return stale data if available
    if (staleData) {
      return {
        data: staleData,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Fetch indicator data for multiple countries (for peer comparison)
 */
export const fetchMultipleCountries = async (
  countryCodes: string[],
  indicatorCode: string,
  year?: number
): Promise<APIResponse<Record<string, number | null>>> => {
  const targetYear = year || new Date().getFullYear() - 1;
  const cacheKey = `wb_multi_${countryCodes.join('_')}_${indicatorCode}_${targetYear}`;

  // Check cache
  const cached = cacheService.getStale<Record<string, number | null>>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  try {
    const wbCountries = countryCodes.map(getWBCountryCode).join(';');
    const url = `${WB_BASE_URL}/country/${wbCountries}/indicator/${indicatorCode}?format=json&per_page=${PER_PAGE}&date=${targetYear - 5}:${targetYear}`;
    const response = await fetchWithRetry(url);
    const allData = parseWBResponse(response);

    // Group by country and get most recent value
    const result: Record<string, number | null> = {};

    // Re-fetch the raw response to get country info
    const rawResponse = await fetch(url);
    const rawData = await rawResponse.json();

    if (rawData && rawData[1]) {
      const countryValues: Record<string, { year: number; value: number | null }> = {};

      for (const item of rawData[1]) {
        const code = item?.countryiso3code || item?.country?.id;
        if (!code) continue;

        const year = parseInt(item.date, 10);
        const value = item.value !== null ? Number(item.value) : null;

        // Keep the most recent non-null value
        if (!countryValues[code] || (value !== null && year > countryValues[code].year)) {
          if (value !== null || !countryValues[code]) {
            countryValues[code] = { year, value };
          }
        }
      }

      // Map back to original country codes
      for (const originalCode of countryCodes) {
        const wbCode = getWBCountryCode(originalCode);
        const entry = countryValues[originalCode] || countryValues[wbCode];
        result[originalCode] = entry?.value ?? null;
      }
    }

    cacheService.set(cacheKey, result, DEFAULT_TTL);

    return {
      data: result,
      error: null,
      source: 'live',
      lastUpdated: new Date(),
      isStale: false,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Fetch a complete country profile with all key indicators
 */
export const fetchCountryProfile = async (
  countryCode: string
): Promise<APIResponse<WBCountryProfile>> => {
  const cacheKey = `wb_profile_${countryCode}`;

  // Check cache
  const cached = cacheService.getStale<WBCountryProfile>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  const currentYear = new Date().getFullYear();
  const indicatorsToFetch = [
    { key: 'gdp', code: WB_INDICATOR_CODES.gdp },
    { key: 'gdpGrowth', code: WB_INDICATOR_CODES.gdp_growth },
    { key: 'gdpPerCapita', code: WB_INDICATOR_CODES.gdp_per_capita },
    { key: 'fdiInflows', code: WB_INDICATOR_CODES.fdi_inflows },
    { key: 'fdiGdp', code: WB_INDICATOR_CODES.fdi_gdp },
    { key: 'population', code: WB_INDICATOR_CODES.population },
    { key: 'populationGrowth', code: WB_INDICATOR_CODES.population_growth },
    { key: 'inflation', code: WB_INDICATOR_CODES.inflation_cpi },
    { key: 'reserves', code: WB_INDICATOR_CODES.reserves },
    { key: 'externalDebt', code: WB_INDICATOR_CODES.external_debt },
    { key: 'externalDebtGdp', code: WB_INDICATOR_CODES.external_debt_gdp },
    { key: 'currentAccount', code: WB_INDICATOR_CODES.current_account_gdp },
    { key: 'exportsGdp', code: WB_INDICATOR_CODES.exports_gdp },
    { key: 'importsGdp', code: WB_INDICATOR_CODES.imports_gdp },
    { key: 'tradeGdp', code: WB_INDICATOR_CODES.trade_gdp },
    { key: 'govtDebtGdp', code: WB_INDICATOR_CODES.govt_debt_gdp },
  ];

  try {
    // Fetch all indicators in parallel
    const results = await Promise.all(
      indicatorsToFetch.map(async ({ key, code }) => {
        if (!code) return { key, value: null, year: null };

        const response = await fetchIndicator(countryCode, code, currentYear - 10, currentYear);
        const latestWithValue = response.data?.find(d => d.value !== null);

        return {
          key,
          value: latestWithValue?.value ?? null,
          year: latestWithValue?.year ?? null,
        };
      })
    );

    // Build the profile object
    const profile: WBCountryProfile = {
      gdp: null,
      gdpGrowth: null,
      gdpPerCapita: null,
      fdiInflows: null,
      fdiGdp: null,
      population: null,
      populationGrowth: null,
      inflation: null,
      reserves: null,
      externalDebt: null,
      externalDebtGdp: null,
      currentAccount: null,
      exportsGdp: null,
      importsGdp: null,
      tradeGdp: null,
      govtDebtGdp: null,
      lastUpdated: new Date(),
      dataYear: currentYear - 1,
    };

    let latestYear = 0;
    for (const result of results) {
      if (result.key in profile) {
        profile[result.key] = result.value;
        if (result.year && result.year > latestYear) {
          latestYear = result.year;
        }
      }
    }
    profile.dataYear = latestYear || currentYear - 1;

    cacheService.set(cacheKey, profile, DEFAULT_TTL);

    return {
      data: profile,
      error: null,
      source: 'live',
      lastUpdated: new Date(),
      isStale: false,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Get the latest value for an indicator
 */
export const fetchLatestValue = async (
  countryCode: string,
  indicatorId: string
): Promise<{ value: number | null; year: number | null }> => {
  const indicatorCode = WB_INDICATOR_CODES[indicatorId];
  if (!indicatorCode) {
    return { value: null, year: null };
  }

  const response = await fetchIndicator(countryCode, indicatorCode, 2015, new Date().getFullYear());

  if (response.data && response.data.length > 0) {
    const latestWithValue = response.data.find(d => d.value !== null);
    return {
      value: latestWithValue?.value ?? null,
      year: latestWithValue?.year ?? null,
    };
  }

  return { value: null, year: null };
};

/**
 * Fetch time series data for charting
 */
export const fetchTimeSeries = async (
  countryCode: string,
  indicatorId: string,
  years: number = 10
): Promise<APIResponse<WBDataPoint[]>> => {
  const indicatorCode = WB_INDICATOR_CODES[indicatorId];
  if (!indicatorCode) {
    return {
      data: null,
      error: `Unknown indicator: ${indicatorId}`,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }

  const currentYear = new Date().getFullYear();
  return fetchIndicator(countryCode, indicatorCode, currentYear - years, currentYear);
};

/**
 * Check if World Bank API is accessible
 */
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(
      `${WB_BASE_URL}/country/US/indicator/NY.GDP.MKTP.CD?format=json&per_page=1&date=2020`,
      { method: 'HEAD' }
    );
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get list of available indicator IDs
 */
export const getAvailableIndicators = (): string[] => {
  return Object.keys(WB_INDICATOR_CODES);
};

/**
 * Get indicator code from ID
 */
export const getIndicatorCode = (indicatorId: string): string | undefined => {
  return WB_INDICATOR_CODES[indicatorId];
};

// Export default object with all functions
const worldBankApi = {
  fetchIndicator,
  fetchMultipleCountries,
  fetchCountryProfile,
  fetchLatestValue,
  fetchTimeSeries,
  checkAPIHealth,
  getAvailableIndicators,
  getIndicatorCode,
  WB_INDICATOR_CODES,
};

export default worldBankApi;
